import { useEffect, useMemo, useState } from "react";
import { api, errorMessage } from "../lib/api";
import type { PublicUser } from "../lib/types";
import { Banner } from "./Banner";
import { useConfirm } from "./Confirm";

interface AssignmentRow {
  id: string;
  status: "OPEN" | "STARTED" | "COMPLETED" | "CANCELLED";
  opensAt: string | null;
  closesAt: string | null;
  student: { id: string; fullName: string; candidateNumber: string | null };
  test: { id: string; title: string; module: string };
}

const STATUS_LABEL: Record<AssignmentRow["status"], string> = {
  OPEN: "Not started",
  STARTED: "Sitting now",
  COMPLETED: "Completed",
  CANCELLED: "Withdrawn",
};

/** `toISOString` needs a real instant; the picker gives local wall-clock time. */
const toIso = (local: string) => (local ? new Date(local).toISOString() : null);

/**
 * A window shown the way a teacher would say it, in the reader's own timezone —
 * which is the one the picker wrote it in.
 */
function describeWindow(
  opensAt: string | null,
  closesAt: string | null,
): string {
  const when = (iso: string) =>
    new Date(iso).toLocaleString(undefined, {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  if (opensAt && closesAt) return `${when(opensAt)} → ${when(closesAt)}`;
  if (opensAt) return `from ${when(opensAt)}`;
  if (closesAt) return `until ${when(closesAt)}`;
  return "any time";
}

/**
 * Setting a whole test on a class.
 *
 * A centre sets Test 1, not three papers in a row over the same register —
 * which is what it used to be: open the Listening, tick thirty names, open the
 * Reading, tick them again. The unit here is the test, so a candidate can
 * never end up holding two thirds of one.
 *
 * Withdrawing works the same way and for the same reason. A paper already sat
 * stays: it is a record of what happened, and cancelling it would erase the
 * fact that they took it.
 */
export function AssignTest({
  setName,
  papers,
  onDone,
}: {
  /** The whole test being set. */
  setName: string;
  /** Its papers, so the panel can say what is ready and what is still a draft. */
  papers: { id: string; module: string; status: string }[];
  onDone?: () => void;
}) {
  const [students, setStudents] = useState<PublicUser[]>([]);
  const [assigned, setAssigned] = useState<AssignmentRow[]>([]);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [opensAt, setOpensAt] = useState("");
  const [closesAt, setClosesAt] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [withdrawing, setWithdrawing] = useState<string | null>(null);
  const { ask, dialog } = useConfirm();

  /** In the order they are sat, whatever order the library handed them over in. */
  const SITTING_ORDER = ["LISTENING", "READING", "WRITING"];
  const inOrder = [...papers].sort(
    (a, b) => SITTING_ORDER.indexOf(a.module) - SITTING_ORDER.indexOf(b.module),
  );
  const ready = inOrder.filter((paper) => paper.status === "PUBLISHED");
  const drafts = inOrder.filter((paper) => paper.status !== "PUBLISHED");

  async function load() {
    try {
      const roster = await api.get<{ members: PublicUser[] }>("/users", {
        params: { role: "STUDENT" },
      });
      setStudents(roster.data.members);

      // Every paper of the test, so "who sits Test 1" is answered by the test
      // rather than by whichever of its papers happens to be on screen.
      const rows = await Promise.all(
        papers.map((paper) =>
          api
            .get<{ assignments: AssignmentRow[] }>("/assignments", {
              params: { testId: paper.id },
            })
            .then(({ data }) => data.assignments),
        ),
      );
      setAssigned(rows.flat().filter((a) => a.status !== "CANCELLED"));
    } catch (err) {
      setError(errorMessage(err, "Could not load your students"));
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setName, papers.map((p) => p.id).join(",")]);

  /**
   * One row per candidate rather than per paper.
   *
   * The test is the unit, so three assignments for one person are one line
   * saying how far through they are.
   */
  const sitting = useMemo(() => {
    const byStudent = new Map<
      string,
      { student: AssignmentRow["student"]; rows: AssignmentRow[] }
    >();
    for (const row of assigned) {
      const entry = byStudent.get(row.student.id) ?? {
        student: row.student,
        rows: [],
      };
      entry.rows.push(row);
      byStudent.set(row.student.id, entry);
    }
    return [...byStudent.values()].sort((a, b) =>
      a.student.fullName.localeCompare(b.student.fullName),
    );
  }, [assigned]);

  const already = new Set(sitting.map((s) => s.student.id));
  const available = students.filter((s) => !already.has(s.id));

  /** The classes still waiting for this test, and who is in each. */
  const classes = useMemo(() => {
    const map = new Map<string, PublicUser[]>();
    for (const student of available) {
      const name = student.groupName ?? "";
      const members = map.get(name);
      if (members) members.push(student);
      else map.set(name, [student]);
    }
    return [...map.entries()]
      .filter(([name]) => name !== "")
      .sort(([a], [b]) => a.localeCompare(b));
  }, [available]);

  function toggleClass(members: PublicUser[]) {
    const next = new Set(chosen);
    const allChosen = members.every((m) => next.has(m.id));
    for (const member of members) {
      if (allChosen) next.delete(member.id);
      else next.add(member.id);
    }
    setChosen(next);
  }

  const windowInvalid = Boolean(
    opensAt && closesAt && new Date(closesAt) <= new Date(opensAt),
  );

  async function assign() {
    if (chosen.size === 0) return;
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post<{
        created: number;
        papers: string[];
        skippedPapers: string[];
      }>("/assignments/set", {
        // The papers themselves, not the name they share: a centre can have
        // its own "Test 1" and a lent "Test 1", and the register on screen
        // knows which three it is showing.
        testIds: papers.map((paper) => paper.id),
        studentIds: [...chosen],
        opensAt: toIso(opensAt),
        closesAt: toIso(closesAt),
      });
      setNotice(
        `${setName} set on ${chosen.size} student${chosen.size === 1 ? "" : "s"}: ` +
          `${data.created} sitting${data.created === 1 ? "" : "s"} across ` +
          `${data.papers.length} paper${data.papers.length === 1 ? "" : "s"}.` +
          (data.skippedPapers.length > 0
            ? ` Not published yet, so left out: ${data.skippedPapers.join(", ")}.`
            : ""),
      );
      setChosen(new Set());
      await load();
      onDone?.();
    } catch (err) {
      setError(errorMessage(err, "Could not set that test"));
    } finally {
      setBusy(false);
    }
  }

  async function withdraw(entry: {
    student: AssignmentRow["student"];
    rows: AssignmentRow[];
  }) {
    const mid = entry.rows.some((row) => row.status === "STARTED");
    const sat = entry.rows.filter((row) => row.status === "COMPLETED").length;

    const yes = await ask({
      title: mid
        ? `${entry.student.fullName} is sitting a paper of ${setName} now. Withdraw it?`
        : `Withdraw ${setName} from ${entry.student.fullName}?`,
      body: (
        <>
          {mid && (
            <p>
              Their sitting ends where it stands and the answers they have typed
              are not marked.
            </p>
          )}
          {sat > 0 && (
            <p>
              The {sat} paper{sat === 1 ? "" : "s"} they have already sat stay
              on their record.
            </p>
          )}
          <p>
            The whole test comes back, not one paper of it. You can set it again
            at any time.
          </p>
        </>
      ),
      confirmLabel: "Withdraw the test",
      tone: "danger",
    });
    if (!yes) return;

    setWithdrawing(entry.student.id);
    setError(null);
    setNotice(null);
    try {
      const { data } = await api.post<{ withdrawn: number; kept: number }>(
        "/assignments/set/withdraw",
        {
          testIds: papers.map((paper) => paper.id),
          setName,
          studentIds: [entry.student.id],
        },
      );
      setNotice(
        `${setName} withdrawn from ${entry.student.fullName}` +
          (data.kept > 0
            ? `; ${data.kept} already sat and stay on the record.`
            : "."),
      );
      await load();
      onDone?.();
    } catch (err) {
      setError(errorMessage(err, "Could not withdraw that test"));
    } finally {
      setWithdrawing(null);
    }
  }

  /** Everyone who still holds a paper of this test that they have not handed in. */
  const withdrawable = sitting.filter((entry) =>
    entry.rows.some((row) => row.status !== "COMPLETED"),
  );

  /**
   * The whole class at once — a test set on the wrong class, or the wrong
   * test on a class, used to mean a Withdraw press per student.
   */
  async function withdrawAll() {
    const mid = withdrawable.filter((entry) =>
      entry.rows.some((row) => row.status === "STARTED"),
    ).length;
    const yes = await ask({
      title: `Withdraw ${setName} from ${withdrawable.length} students?`,
      body: (
        <>
          {mid > 0 && (
            <p>
              {mid} {mid === 1 ? "is" : "are"} sitting a paper of it now. Those
              papers stop where they are and are not marked.
            </p>
          )}
          <p>
            Papers already handed in stay on the record. You can set it again at
            any time.
          </p>
        </>
      ),
      confirmLabel: "Withdraw from everyone",
      tone: "danger",
    });
    if (!yes) return;

    setWithdrawing("all");
    setError(null);
    setNotice(null);
    try {
      const { data } = await api.post<{ withdrawn: number; kept: number }>(
        "/assignments/set/withdraw",
        {
          testIds: papers.map((paper) => paper.id),
          setName,
          studentIds: withdrawable.map((entry) => entry.student.id),
        },
      );
      setNotice(
        `${setName} withdrawn from ${withdrawable.length} students` +
          (data.kept > 0
            ? `; ${data.kept} paper${data.kept === 1 ? "" : "s"} already sat stay on the record.`
            : "."),
      );
      await load();
      onDone?.();
    } catch (err) {
      setError(errorMessage(err, "Could not withdraw that test"));
    } finally {
      setWithdrawing(null);
    }
  }

  if (ready.length === 0) {
    return (
      <section className="panel">
        {dialog}
        <h2 className="panel__title">Setting {setName}</h2>
        <p className="muted" style={{ maxWidth: "62ch" }}>
          None of its papers is published yet. A draft cannot be set, so nobody
          can sit a paper that is still being corrected.
        </p>
      </section>
    );
  }

  return (
    <section className="panel stack">
      {dialog}
      <div>
        <h2 className="panel__title">Who sits {setName}</h2>
        <p className="muted" style={{ maxWidth: "64ch" }}>
          The whole test goes at once —{" "}
          {ready.map((p) => p.module.toLowerCase()).join(", ")} — and a
          candidate sits them in order. Each paper can be sat once; the clock
          starts when they begin and cannot be paused.
          {drafts.length > 0 && (
            <>
              {" "}
              Still in draft, so left out until published:{" "}
              {drafts.map((p) => p.module.toLowerCase()).join(", ")}.
            </>
          )}
        </p>
      </div>

      {error && <Banner tone="error">{error}</Banner>}
      {notice && <Banner tone="success">{notice}</Banner>}

      {available.length > 0 && (
        <div className="stack" style={{ gap: "var(--s-3)" }}>
          {classes.length > 0 && (
            <div className="groupbar__chips">
              <span className="eyebrow">Classes</span>
              {classes.map(([name, members]) => (
                <button
                  key={name}
                  type="button"
                  className={`chip ${members.every((m) => chosen.has(m.id)) ? "chip--on" : ""}`}
                  onClick={() => toggleClass(members)}
                >
                  {name} <span className="mono">{members.length}</span>
                </button>
              ))}
            </div>
          )}

          <div className="picker">
            {available.map((student) => (
              <label key={student.id} className="picker__row">
                <input
                  type="checkbox"
                  aria-label={student.fullName}
                  checked={chosen.has(student.id)}
                  onChange={() => {
                    const next = new Set(chosen);
                    if (next.has(student.id)) next.delete(student.id);
                    else next.add(student.id);
                    setChosen(next);
                  }}
                />
                <span>
                  <span style={{ fontWeight: 600 }}>{student.fullName}</span>
                  <span className="muted">
                    {" "}
                    ·{" "}
                    <span className="mono">
                      {student.candidateNumber ?? "—"}
                    </span>
                    {student.groupName ? ` · ${student.groupName}` : ""}
                  </span>
                </span>
              </label>
            ))}
          </div>

          <div className="form-grid">
            <label className="field">
              <span className="field__label">Opens</span>
              <input
                type="datetime-local"
                className="field__input"
                value={opensAt}
                onChange={(e) => setOpensAt(e.target.value)}
              />
              <span className="field__hint">
                Leave empty to let them start straight away.
              </span>
            </label>
            <label className="field">
              <span className="field__label">Closes</span>
              <input
                type="datetime-local"
                className="field__input"
                value={closesAt}
                onChange={(e) => setClosesAt(e.target.value)}
              />
              <span className="field__hint">
                {windowInvalid
                  ? "The closing time must be after the opening one."
                  : "Optional."}
              </span>
            </label>
          </div>

          <div className="row">
            <button
              className="btn btn--primary"
              disabled={busy || chosen.size === 0 || windowInvalid}
              onClick={() => void assign()}
            >
              {busy
                ? "Setting…"
                : `Set ${setName} on ${chosen.size} student${chosen.size === 1 ? "" : "s"}`}
            </button>
          </div>
        </div>
      )}

      {withdrawable.length > 1 && (
        <div className="row" style={{ justifyContent: "space-between" }}>
          <p className="muted" style={{ margin: 0 }}>
            {withdrawable.length} students still have papers of this test to
            sit.
          </p>
          <button
            className="btn btn--danger"
            disabled={withdrawing !== null}
            onClick={() => void withdrawAll()}
          >
            {withdrawing === "all" ? "Withdrawing…" : "Withdraw from everyone"}
          </button>
        </div>
      )}

      {sitting.length === 0 ? (
        <p className="muted">Nobody has been given this test yet.</p>
      ) : (
        <div className="ledger__wrap">
          <table className="ledger">
            <thead>
              <tr>
                <th className="num">Candidate</th>
                <th>Name</th>
                <th>Available</th>
                <th>How far</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {sitting.map((entry) => {
                const done = entry.rows.filter(
                  (r) => r.status === "COMPLETED",
                ).length;
                const first = entry.rows[0]!;
                return (
                  <tr key={entry.student.id}>
                    <td className="num">
                      {entry.student.candidateNumber ?? "—"}
                    </td>
                    <td style={{ fontWeight: 600 }}>
                      {entry.student.fullName}
                    </td>
                    <td className="mono muted">
                      {describeWindow(first.opensAt, first.closesAt)}
                    </td>
                    <td>
                      <span className="mono">
                        {done}/{entry.rows.length}
                      </span>{" "}
                      <span className="muted">
                        {entry.rows.some((r) => r.status === "STARTED")
                          ? STATUS_LABEL.STARTED.toLowerCase()
                          : done === entry.rows.length
                            ? "finished"
                            : "papers sat"}
                      </span>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {/* Nothing to take back from someone who has sat it all. */}
                      {done < entry.rows.length && (
                        <button
                          className="btn btn--danger"
                          disabled={withdrawing === entry.student.id}
                          onClick={() => void withdraw(entry)}
                        >
                          {withdrawing === entry.student.id
                            ? "Withdrawing…"
                            : "Withdraw"}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
